import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

const STEPS = ["Confirm track", "Nutrition mode", "Bodyweight baseline", "Step baseline", "Recovery baseline"];

export default function LeanStart() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    track: "GYM",
    nutrition_mode: "SIMPLE",
    bodyweight_kg: "",
    starting_step_target: 8000,
    protein_target_g: 130,
    calorie_target: "",
    sleep: "Okay",
    energy: "Normal",
    soreness: "Low",
    stress: "Low",
  });

  const next = () => setStep((s) => Math.min(s+1, STEPS.length-1));
  const back = () => setStep((s) => Math.max(s-1, 0));

  const submit = async () => {
    if (!form.bodyweight_kg) { toast.error("Enter your current bodyweight."); return; }
    setBusy(true);
    try {
      await api.post("/lean/start", {
        track: form.track,
        nutrition_mode: form.nutrition_mode,
        bodyweight_kg: parseFloat(form.bodyweight_kg),
        starting_step_target: parseInt(form.starting_step_target),
        protein_target_g: parseInt(form.protein_target_g),
        calorie_target: form.nutrition_mode === "PRECISION" && form.calorie_target ? parseInt(form.calorie_target) : null,
        sleep: form.sleep, energy: form.energy, soreness: form.soreness, stress: form.stress,
      });
      toast.success("LEAN pathway started. Welcome to Week 1.");
      nav("/app/lean", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <div className="label-eyebrow">Start LEAN Pathway</div>
        <h1 className="wordmark mt-2 text-4xl md:text-5xl uppercase">Set your <span className="text-primary">baseline.</span></h1>
        <p className="mt-3 text-sm text-foreground/70">You can change nutrition mode later without resetting the block.</p>
      </div>

      {/* Progress */}
      <div className="flex gap-2" data-testid="lean-start-steps">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded ${i <= step ? "bg-primary" : "bg-muted"}`} />
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg p-6 min-h-[280px]">
        {step === 0 && (
          <div className="space-y-4" data-testid="step-track">
            <div className="label-eyebrow">1 · Track</div>
            <p className="text-sm text-foreground/70">Which track are you training on?</p>
            <div className="grid grid-cols-2 gap-3">
              {["GYM","HOME"].map((t) => (
                <button key={t} onClick={() => setForm({...form, track: t})}
                  className={`rounded-lg border-2 p-6 text-left transition-all ${form.track === t ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}
                  data-testid={`track-${t.toLowerCase()}`}>
                  <div className="font-head uppercase text-lg">{t}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t === "GYM" ? "Full commercial gym equipment." : "Dumbbells, bands, bodyweight."}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="step-nutrition">
            <div className="label-eyebrow">2 · Nutrition Mode</div>
            <p className="text-sm text-foreground/70">You do not have to count calories. But fat loss still requires a deficit.</p>
            <div className="grid gap-3">
              {[
                {v:"SIMPLE",   t:"Simple",    d:"Protein target, BA30 plate/hand method, default meals, portion adjustments."},
                {v:"PRECISION",t:"Precision", d:"Calorie + protein targets displayed. Log calories/macros manually."},
              ].map((o) => (
                <button key={o.v} onClick={() => setForm({...form, nutrition_mode: o.v})}
                  className={`rounded-lg border-2 p-5 text-left ${form.nutrition_mode === o.v ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}
                  data-testid={`mode-${o.v.toLowerCase()}`}>
                  <div className="font-head uppercase text-lg">{o.t}</div>
                  <p className="text-xs text-muted-foreground mt-1">{o.d}</p>
                </button>
              ))}
            </div>
            {form.nutrition_mode === "PRECISION" && (
              <div className="pt-2">
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Starting calorie target (kcal/day, optional)</label>
                <input type="number" value={form.calorie_target} onChange={(e) => setForm({...form, calorie_target: e.target.value})}
                  placeholder="e.g. 2000" className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2.5" data-testid="calorie-target" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Daily protein target (g)</label>
              <input type="number" value={form.protein_target_g} onChange={(e) => setForm({...form, protein_target_g: e.target.value})}
                className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2.5" data-testid="protein-target" />
              <p className="mt-1 text-[11px] text-muted-foreground">Guidance: ~1.6–2.2 g per kg bodyweight per day.</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="step-bodyweight">
            <div className="label-eyebrow">3 · Bodyweight Baseline</div>
            <p className="text-sm text-foreground/70">Weigh in consistent conditions (same time, similar state). Trend beats any single day.</p>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Current bodyweight (kg)</label>
              <input type="number" step="0.1" value={form.bodyweight_kg} onChange={(e) => setForm({...form, bodyweight_kg: e.target.value})}
                placeholder="e.g. 82.5" className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2.5" data-testid="bodyweight" required />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-steps">
            <div className="label-eyebrow">4 · Step Baseline</div>
            <p className="text-sm text-foreground/70">Use your recent 10K30 average, or a realistic current baseline. This becomes your dynamic fat-loss lever — not an auto-escalated staircase.</p>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Starting step target (per day)</label>
              <input type="number" step="100" value={form.starting_step_target} onChange={(e) => setForm({...form, starting_step_target: e.target.value})}
                className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2.5" data-testid="step-target" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="step-recovery">
            <div className="label-eyebrow">5 · Recovery Baseline</div>
            <p className="text-sm text-foreground/70">Set how you're doing right now. Poor recovery can hold back the deficit.</p>
            <SelectRow label="Sleep"    val={form.sleep}    options={["Poor","Okay","Good"]}     onChange={(v)=>setForm({...form,sleep:v})}    testid="sleep"/>
            <SelectRow label="Energy"   val={form.energy}   options={["Low","Normal","High"]}    onChange={(v)=>setForm({...form,energy:v})}   testid="energy"/>
            <SelectRow label="Soreness" val={form.soreness} options={["High","Moderate","Low"]}  onChange={(v)=>setForm({...form,soreness:v})} testid="soreness"/>
            <SelectRow label="Stress"   val={form.stress}   options={["High","Moderate","Low"]}  onChange={(v)=>setForm({...form,stress:v})}   testid="stress"/>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={back} disabled={step === 0}
          className="text-sm text-muted-foreground disabled:opacity-30 flex items-center gap-1" data-testid="lean-start-back">
          <ArrowLeft size={14}/> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} className="btn-primary" data-testid="lean-start-next">
            Next <ArrowRight size={14} className="ml-1"/>
          </button>
        ) : (
          <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="lean-start-submit">
            {busy ? "Starting…" : "Start Week 1"}
          </button>
        )}
      </div>
    </div>
  );
}

function SelectRow({ label, val, options, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</label>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {options.map((o) => (
          <button key={o} onClick={() => onChange(o)}
            className={`rounded-md border py-2 text-sm font-head uppercase tracking-wider ${val === o ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`}
            data-testid={`${testid}-${o.toLowerCase()}`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
